<template>
  <div
    class="bg-gradient-black-01 flex flex-wrap items-end rounded-xl px-6 pb-4 pt-10 ring-1 ring-gray-200/10 lg:px-8 lg:pt-12">
    <div class="grid w-full gap-x-8 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
      <h2 class="ml-2 self-center text-2xl text-white md:ml-4 md:text-3xl">
        {{ title }}
      </h2>
      <Dropdown
        :items="AREAS"
        :model-value="area"
        :placeholder="AreaLabel.ALL"
        class="z-[12]"
        @update:model-value="item => setSelected(item, JobFilterId.AREA)" />
      <Dropdown
        :items="LOCATIONS"
        :model-value="location"
        :placeholder="LocationLabel.ALL"
        class="z-[11]"
        @update:model-value="item => setSelected(item, JobFilterId.LOCATION)" />
      <Dropdown
        :items="DIVISIONS"
        :model-value="division"
        :placeholder="DivisionLabel.ALL"
        class="z-10"
        @update:model-value="item => setSelected(item, JobFilterId.DIVISION)" />
      <Dropdown
        :items="EMPLOYMENT_TYPES"
        :model-value="employmentType"
        :placeholder="EmploymentTypeLabel.ALL"
        class="z-10"
        @update:model-value="item => setSelected(item, JobFilterId.EMPLOYMENT_TYPE)" />
      <Dropdown
        :items="WORK_TYPES"
        :model-value="workTypes"
        :placeholder="WorkTypeLabel.ALL"
        multiple
        class="z-9"
        @update:model-value="item => setSelected(item, JobFilterId.WORK_TYPE)" />
    </div>
    <ClientOnly>
      <button
        class="bottom-line-inverted relative ml-auto mt-4 pb-0.5 text-sm text-white duration-500"
        :class="
          area || location || division || employmentType || workTypes?.length ? '' : 'pointer-events-none opacity-0'
        "
        @click="clearFilter">
        Clear filters
      </button>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
  import {
    AreaLabel,
    AREAS,
    DivisionLabel,
    DIVISIONS,
    EMPLOYMENT_TYPES,
    EmploymentTypeLabel,
    JobFilterId,
    LocationLabel,
    LOCATIONS,
    WORK_TYPES,
    WorkTypeLabel
  } from '~/constants/jobs'

  import type { ComboboxItem } from '../../../models/combobox'

  type filterProps = {
    title: string
    area: ComboboxItem
    location: ComboboxItem
    division: ComboboxItem
    employmentType: ComboboxItem
    workTypes: ComboboxItem[]
  }

  defineProps<filterProps>()
  const route = useRoute()
  const router = useRouter()

  defineExpose({
    clearFilter
  })

  function clearFilter() {
    router.push({
      query: {
        ...route.query,
        area: undefined,
        location: undefined,
        division: undefined,
        employmentType: undefined,
        workType: []
      }
    })
  }

  function setSelected(item: ComboboxItem | ComboboxItem[], id: JobFilterId) {
    const newRouteLocation = {
      query: { ...route.query }
    }

    if (!item) {
      newRouteLocation.query[id] = undefined
    } else if (Array.isArray(item)) {
      newRouteLocation.query[id] = item.map(item => item.key).join(',')
    } else {
      newRouteLocation.query[id] = item.key
    }

    router.push(newRouteLocation)
  }
</script>

<style>
  .bottom-line-inverted:before,
  .bottom-line-inverted:after {
    @apply absolute bottom-0 h-px w-full bg-white;
    content: '';
  }

  .bottom-line-inverted:before {
    @apply left-0 bg-white;
    transition: width 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
  }
  .bottom-line-inverted:after {
    @apply right-0 w-full bg-transparent;
    transition: 0s;
  }

  .bottom-line-inverted:hover:before {
    @apply w-0 bg-transparent;
  }

  .bottom-line-inverted:hover:after {
    @apply w-0 bg-white;
    transition: width 0.4s cubic-bezier(0.22, 0.61, 0.36, 1);
  }
</style>
